import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Shield, Zap, Hexagon, Loader2 } from "lucide-react";

const AIBlueTeam = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/blue-team/overview").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-6 text-cyan-400"><Loader2 className="animate-spin h-5 w-5" /></div>;

  const m = data.metrics;

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Defence / Blue Team"
        title="AI Blue Team"
        subtitle="Automated triage, containment and hunt-query orchestration across your SIEM stack."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          ["Auto-Containments (24h)", m.auto_containments_24h, "cyan", Shield],
          ["Mean Time to Respond", `${m.mean_time_to_respond_seconds}s`, "fuchsia", Zap],
          ["Active Playbooks", m.active_playbooks, "emerald", Hexagon],
          ["SIEM Connectors", m.siem_connectors.length, "amber", Hexagon],
        ].map(([k, v, c, Icon]) => (
          <div key={k} className="csx-card p-4">
            <Icon className={`h-4 w-4 text-${c}-400 mb-2`} />
            <div className={`font-display text-3xl font-black text-${c}-400`}>{v}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{k}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 csx-card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 text-[10px] uppercase tracking-[0.3em] text-cyan-400">
            Automated Playbooks
          </div>
          <ul>
            {data.playbooks.map((p) => (
              <li key={p.id} data-testid={`bt-playbook-${p.id}`} className="px-4 py-3 border-b border-slate-900 hover:bg-slate-900/40">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-slate-100 font-bold truncate">{p.name}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">trigger: <span className="font-mono text-cyan-300">{p.trigger}</span></div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/50 bg-emerald-500/10 px-2 py-0.5">
                    {p.status}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.actions.map((a) => (
                    <span key={a} className="text-[10px] tracking-widest px-1.5 py-0.5 bg-slate-900 border border-slate-800 text-slate-400">
                      {a}
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex justify-between text-[10px] uppercase tracking-widest text-slate-500">
                  <span>runs / 24h: <span className="text-cyan-300">{p.runs_24h}</span></span>
                  <span>false-positive: <span className={p.false_positive_rate > 3 ? "text-amber-300" : "text-emerald-300"}>{p.false_positive_rate}%</span></span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="csx-card p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 mb-3">Hunt Queries (Live)</div>
          <ul className="space-y-2">
            {data.hunt_queries.map((h) => (
              <li key={h.id} data-testid={`bt-hunt-${h.id}`} className="border border-slate-800 p-3 bg-slate-950/40">
                <div className="text-sm text-slate-100">{h.name}</div>
                <div className="text-[11px] text-slate-400 mt-1 flex justify-between">
                  <span>{h.platform} · <span className="font-mono text-cyan-300">{h.ttp}</span></span>
                  <span className={h.matches_24h > 0 ? "text-rose-300" : "text-emerald-300"}>{h.matches_24h} matches</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Connectors</div>
            <div className="flex flex-wrap gap-1.5">
              {m.siem_connectors.map((s) => (
                <span key={s} className="text-[10px] tracking-widest border border-cyan-500/40 text-cyan-300 px-2 py-0.5">
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIBlueTeam;
